<script setup lang="ts">
import { VisionAIFooterButton } from '@dashhudson/dashing-ui/button';
import { DropdownBase } from '@dashhudson/dashing-ui/dropdown-panel';
import { InsightDropdownPanel } from '@dashhudson/dashing-ui/insight-dropdown';
import { useDashToast } from '@dashhudson/dashing-ui/toast';
import { computed, ref, watch } from 'vue';

import { useViewInsight } from '@/components/VisionAi/composables/useViewInsight';
import { useVisionFloatingButtonLayout } from '@/composables/layout/useFloatingWidgetLayout';
import { insightDisplayLabel, useActiveInsight } from '@/shared/queries/use-active-insight';
import { useRecordInsightInteraction } from '@/shared/queries/use-record-insight-interaction';
import { useInsightSessionStore } from '@/stores/insight-session';
import { useVisionAiStore } from '@/stores/vision-ai';
import { useVisionAiConversationsStore } from '@/stores/vision-ai-conversations';

const emit = defineEmits<{
  'toggle-chat-window': [];
}>();

const { isChatPanelOpen, chatPanelTransformStyle, buttonPositionClasses } =
  useVisionFloatingButtonLayout();

const visionAiStore = useVisionAiStore();
const visionAiConversationsStore = useVisionAiConversationsStore();
const { activeInsight } = useActiveInsight();
const { recordInteraction } = useRecordInsightInteraction();
const { viewInsight } = useViewInsight();
const { markInsightDismissed } = useInsightSessionStore();
const { addToast } = useDashToast({ life: 5000 });

const popoverShown = ref(false);
const INSIGHT_DROPDOWN_ANCHOR = '[data-test="vision-ai-button-global"]';
const INSIGHT_DROPDOWN_TARGETS = '[data-test="VisionAIFooterButtonCta"]';

const showInsightPill = computed(
  () => !isChatPanelOpen.value && visionAiStore.isPendingCompletionPillExpanded,
);

const showLoadingPill = computed(
  () => !isChatPanelOpen.value && visionAiStore.isLoadingPillExpanded,
);

const isTaskInProgress = computed(
  () => visionAiConversationsStore.loadingConversationIds.length > 0,
);

// The completion and loading pills always have higher priority, and the
// active-insight pill never surfaces while the chat panel is open.
const showActiveInsightPill = computed(
  () =>
    !isChatPanelOpen.value &&
    !showInsightPill.value &&
    !showLoadingPill.value &&
    activeInsight.value !== null,
);

const expanded = computed(
  () => showInsightPill.value || showLoadingPill.value || showActiveInsightPill.value,
);

// Insight Pill keeps the animation icon (loading=false). Loading Pill and the
// collapsed in-flight state both render the spinner (loading=true). When the
// panel is open, the button shows the chevron-right regardless of task state.
const loadingProp = computed(() => {
  if (isChatPanelOpen.value) return false;
  if (showInsightPill.value) return false;
  if (showLoadingPill.value) return true;
  return isTaskInProgress.value;
});

const statusMessage = computed(() => {
  if (showInsightPill.value) return 'Vision has finished a task';
  if (showLoadingPill.value) return 'Vision is working on a task...';
  if (showActiveInsightPill.value && activeInsight.value) {
    return insightDisplayLabel(activeInsight.value);
  }
  return undefined;
});

const callToAction = computed(() => {
  if (showInsightPill.value) {
    return { label: 'View Response', icon: 'chevron-right' } as const;
  }
  if (showLoadingPill.value) {
    return { label: 'Open Vision', icon: 'chevron-right' } as const;
  }
  if (showActiveInsightPill.value) {
    return {
      label: popoverShown.value ? 'See Less' : 'See More',
      icon: 'chevron-up',
      iconClass: { 'rotate-180': popoverShown.value },
    } as const;
  }
  return undefined;
});

async function openPendingCompletionConversation() {
  if (!visionAiStore.hasPendingCompletion) return;
  const conversationId = visionAiStore.pendingCompletionConversationId;
  if (!conversationId) return;

  try {
    await visionAiConversationsStore.getConversationMessages({ conversationId });
    visionAiConversationsStore.setCurrentConversationId(conversationId);
    visionAiStore.consumePendingCompletion();
    visionAiStore.markConversationRead(conversationId);
  } catch {
    addToast({
      message: 'Could not load this conversation. Please try again.',
      severity: 'error',
    });
    // Fall through to open the panel so the user isn't trapped. The
    // watcher will clearPendingCompletion; the unread dot remains so the
    // sidebar conversation click is the retry path.
  }
}

async function handleButtonClicked(event: Event) {
  event.stopPropagation();
  if (!isChatPanelOpen.value) {
    await openPendingCompletionConversation();
  }
  emit('toggle-chat-window');
}

async function handleCtaClick(event: MouseEvent) {
  event.stopPropagation();
  // The active-insight CTA opens/closes the popover; every other pill state
  // routes the CTA click through the same path as the main button (open the
  // chat, consume pending completion if present).
  if (showActiveInsightPill.value && activeInsight.value) {
    popoverShown.value = !popoverShown.value;
    return;
  }
  await handleButtonClicked(event);
}

function handleViewInsight() {
  const insight = activeInsight.value;
  popoverShown.value = false;
  if (insight) {
    markInsightDismissed(insight.id);
    void viewInsight(insight);
  }
  emit('toggle-chat-window');
}

function handlePopoverClose() {
  const insightId = activeInsight.value?.id;
  popoverShown.value = false;
  if (insightId) {
    markInsightDismissed(insightId);
    recordInteraction({ insightId, interactionType: 'dismissed' });
  }
}

watch(activeInsight, (insight) => {
  if (!insight) popoverShown.value = false;
});

const recordedFirstViewIds = new Set<number>();
watch(
  [activeInsight, showActiveInsightPill],
  ([insight, visible]) => {
    if (!visible || !insight) return;
    if (recordedFirstViewIds.has(insight.id)) return;
    recordedFirstViewIds.add(insight.id);
    recordInteraction({ insightId: insight.id, firstViewedAt: new Date().toISOString() });
  },
  { immediate: true },
);

watch(isChatPanelOpen, (open) => {
  if (open) popoverShown.value = false;
});
</script>

<template>
  <div
    data-test="vision-ai-button-global"
    data-exclude-side-panel-close
    :class="['vision-ai-button pointer-events-auto relative', buttonPositionClasses]"
    :style="[{ zIndex: '9999' }, chatPanelTransformStyle]"
  >
    <VisionAIFooterButton
      :open="isChatPanelOpen"
      :loading="loadingProp"
      :expanded="expanded"
      :status-message="statusMessage"
      :call-to-action="callToAction"
      :class="expanded ? 'hover:scale-100!' : undefined"
      enable-animation
      @click="handleButtonClicked"
      @cta-click="handleCtaClick"
    />
    <DropdownBase
      v-if="showActiveInsightPill && activeInsight"
      v-model:shown="popoverShown"
      placement="top-end"
      :triggers="[]"
      :reference-node="INSIGHT_DROPDOWN_ANCHOR"
      :target-nodes="INSIGHT_DROPDOWN_TARGETS"
      :distance="24"
      data-test="vision-insight-popover"
    >
      <template #content>
        <InsightDropdownPanel @close="handlePopoverClose" @view-insight="handleViewInsight">
          {{ activeInsight.headline }}
        </InsightDropdownPanel>
      </template>
    </DropdownBase>
  </div>
</template>
